import { useState, useCallback, type ReactElement } from 'react'
import { ScanInstructionsScreen } from './ScanInstructionsScreen.js'
import { useStep } from '../state/useStep.js'
import { useDemoMode } from '../state/useDemoMode.js'
import { useDemoStore, type ValveType, type DrainType, type OutletSide } from '../state/store.js'
import { LiveCameraScan } from '../live/LiveCameraScan.js'
import { DemoVideoScan } from '../live/DemoVideoScan.js'
import { SCAN_CONFIGS } from '../live/scans.js'
import { useGrantLifetime, type CameraGrant } from '../live/useRequestCameraGrant.js'

const CONFIG = SCAN_CONFIGS.undersink

interface UndersinkFindings {
  valve: ValveType
  drain: DrainType
  outlet: { present: boolean; side: OutletSide }
  drainLoopInches: number
}

export const ScanUndersinkScreen = (): ReactElement => {
  const { goto } = useStep()
  const { dispatch } = useDemoStore()
  const [phase, setPhase] = useState<'instructions' | 'scanning'>('instructions')
  const [grant, setGrant] = useState<CameraGrant | null>(null)
  const demo = useDemoMode()

  useGrantLifetime(grant)

  const onComplete = useCallback((findings: unknown) => {
    dispatch({ type: 'UNDERSINK_DETECTED', findings: findings as UndersinkFindings })
    goto('undersink-results')
  }, [dispatch, goto])

  const onSkipOrAbort = useCallback(() => {
    dispatch({ type: 'UNDERSINK_DETECTED', findings: {
      valve: 'quarter-turn', drain: 'standard-hose',
      outlet: { present: true, side: 'right' }, drainLoopInches: 18,
    } })
    goto('undersink-results')
  }, [dispatch, goto])

  const onProceed = useCallback((g: CameraGrant | null) => {
    dispatch({ type: 'CLEAR_LIVE_PATCH', scan: 'undersink' })
    if (g) setGrant(g)
    setPhase('scanning')
  }, [dispatch])

  if (phase === 'instructions') {
    return (
      <ScanInstructionsScreen
        title="Under-sink scan instructions"
        backTo="countertop-results"
        headline="Now let's check under the sink"
        body="Open the cabinet under your kitchen sink and point the camera inside. We'll check shutoff valve, drain hookup, outlet, and drain loop height — the details that most often delay installs when they're wrong."
        tip="A flashlight can help if it's dark under there."
        ctaLabel="Start Scan"
        hero={
          <div className="grid grid-cols-2 gap-2">
            <div className="border-2 border-dashed border-primary rounded-xl w-24 h-28" />
            <div className="flex flex-col gap-2">
              <div className="border-2 border-dashed border-primary rounded-full w-10 h-16" />
              <div className="border-2 border-dashed border-primary rounded-full w-10 h-10" />
            </div>
          </div>
        }
        onProceed={onProceed}
        onSkip={onSkipOrAbort}
      />
    )
  }

  if (demo) {
    return (
      <DemoVideoScan
        scanConfig={CONFIG}
        onComplete={onComplete}
        onAbort={onSkipOrAbort}
      />
    )
  }

  if (!grant) return null as unknown as ReactElement
  return <LiveCameraScan scanConfig={CONFIG} grant={grant} onComplete={onComplete} onAbort={onSkipOrAbort} />
}
